import { DocumentNode } from 'graphql';
import { Edit, Users } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { GetTeamQuery as GetTeam, Permission } from '__generated__/graphql';

import { ContainerWithOptions, Option } from '../../../common/Common/OptionsDropdown';
import { TicketLabel } from '../../../common/Common/SellableLabel';
import { encodeUrl } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import { useSession } from '../../Auth/SessionProvider';
import EditBackendTeamMutation from './EditBackendTeamMutation';
import GetTeamQuery from './GetTeamQuery';
import UI from '../../../common/UI';
import useEditTeamModal from '../Participant/About/useEditTeamModal';
import useLocale from '../../../common/useLocale';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';

type Team = GetTeam['team'];
type Registration = Team['registrations'][0];

export interface TeamMemberCardProps {
  team: Team;
  registration: Registration;
  refetchQueries?: DocumentNode[];
}

const TeamMemberCard = ({
  team, registration, refetchQueries = [GetTeamQuery],
}: TeamMemberCardProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();
  const { hasPermission } = useSession();
  const notifier = useNotifier();
  const project = useProject();

  const hasEditPermission = hasPermission(project.id, [Permission.edit_registrations]);

  const { participant } = registration;

  const [editCaptain] = useMutation(EditBackendTeamMutation, {
    variables: {
      input: {
        id: team.id,
        title: team.title,
        captain: {
          id: participant.id,
        },
      },
    },
    onCompleted: () => {
      notifier.success(t('notifications.team_captain_changed'));
    },
  });

  const [openEditTeamModal] = useEditTeamModal({
    registrationId: registration.id,
    refetchQueries,
  });

  const participantPageUrl = `/${project.id}/participants/${participant.id}?${encodeUrl({
    event: team.event.id,
    search: `ATL${registration.sequence}`,
  })}`;

  const isDisabled = !registration.purchase.active;

  return (
    <UI.NavCard to={participantPageUrl}>
      <ContainerWithOptions
        renderOptions={hasEditPermission ? () => (
          <>
            <Option
              icon={<Users />}
              label={t('assign_team_captain')}
              onClick={() => editCaptain()}
              disabled={team.captain.id === participant.id}
            />
            <Option
              icon={<Edit />}
              label={t('edit_team_membership')}
              onClick={() => openEditTeamModal()}
            />
          </>
        ) : undefined}
      >
        <UI.GridContainer sc={{ columns: ['1fr', '1fr fit-content(120px)'], gutter: 0.5 }}>
          <UI.Div sc={{ column: ['1 / span 2', '1'] }}>
            <UI.H4>
              <UI.Span sc={{ muted: !registration.assigned || isDisabled, deleted: isDisabled, mr: 1 }}>
                {registration.assigned
                  ? registration.full_name
                  : (registration.invited_email || registration.participant.email)}
              </UI.Span>
              {' '}
              <UI.Span sc={{ fontWeight: 400, muted: true }}>
                {registration.assigned ? registration.participant.email : (registration.invited_email
                  ? t('invited')
                  : t('not_assigned_yet'))}
              </UI.Span>
            </UI.H4>
            <TicketLabel ticket={registration.ticket.title} status={registration.status} sc={{ mt: 1 }} />
          </UI.Div>
          <UI.Div sc={{ small: true, textAlign: ['left', 'right'], mt: 0.5 }}>
            <UI.Div sc={{ fontWeight: 500 }}>
              ATL
              {registration.sequence}
            </UI.Div>
            <UI.Div sc={{ mt: 0.5, muted: true }}>
              {formatDate(
                parseDate(registration.created_at, { timezone: 'UTC' }),
                { format: 'display_compact_date' },
              )}
            </UI.Div>
          </UI.Div>
        </UI.GridContainer>
      </ContainerWithOptions>
    </UI.NavCard>
  );
};

export default TeamMemberCard;
